import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { 
  SettlementItem, 
  SettlementSearchMeta, 
  SearchSettlementsResponse,
  CommissionSumResponse,
  CommissionSumItem,
  CommissionSumMeta,
  CommissionSumExtra,
  CommissionSearchResponse,
  CommissionSearchItem,
  CommissionSearchMeta
} from '../Domain/entity/SettlementTypes';
import { searchSettlements, getCommissionSum, searchCommissions } from './settlementActions';

type SearchStatus = 'idle' | 'searching' | 'searched' | 'failed';
type CommissionSumStatus = 'idle' | 'loading' | 'loaded' | 'failed';
type CommissionSearchStatus = 'idle' | 'searching' | 'searched' | 'failed';

export type SettlementState = {
  // Settlement 검색 관련
  searchStatus: SearchStatus;
  searchError?: string;
  searchResults: SettlementItem[];
  searchMeta?: SettlementSearchMeta;
  // 후원금 내역 합계 관련
  commissionSumStatus: CommissionSumStatus;
  commissionSumError?: string;
  commissionSumResults: CommissionSumItem[];
  commissionSumMeta?: CommissionSumMeta;
  commissionSumExtra?: CommissionSumExtra;
  // 후원금 내역 조회 관련
  commissionSearchStatus: CommissionSearchStatus;
  commissionSearchError?: string;
  commissionSearchResults: CommissionSearchItem[];
  commissionSearchMeta?: CommissionSearchMeta;
};

const initialState: SettlementState = {
  searchStatus: 'idle',
  searchResults: [],
  commissionSumStatus: 'idle',
  commissionSumResults: [],
  commissionSearchStatus: 'idle',
  commissionSearchResults: [],
};

const settlementSlice = createSlice({
  name: 'settlement',
  initialState,
  reducers: {
    resetSettlementState() {
      return initialState;
    },
    clearSearchResults(state) {
      state.searchStatus = 'idle';
      state.searchError = undefined;
      state.searchResults = [];
      state.searchMeta = undefined;
    },
    clearCommissionSum(state) {
      state.commissionSumStatus = 'idle';
      state.commissionSumError = undefined;
      state.commissionSumResults = [];
      state.commissionSumMeta = undefined;
      state.commissionSumExtra = undefined;
    },
    clearCommissionSearch(state) {
      state.commissionSearchStatus = 'idle';
      state.commissionSearchError = undefined;
      state.commissionSearchResults = [];
      state.commissionSearchMeta = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // searchSettlements
      .addCase(searchSettlements.pending, (state) => {
        state.searchStatus = 'searching';
        state.searchError = undefined;
      })
      .addCase(searchSettlements.fulfilled, (state, action: PayloadAction<SearchSettlementsResponse>) => {
        state.searchStatus = 'searched';
        state.searchResults = action.payload.data;
        state.searchMeta = action.payload.meta;
      })
      .addCase(searchSettlements.rejected, (state, action) => {
        state.searchStatus = 'failed';
        state.searchError = action.payload ?? 'Settlement 검색에 실패했습니다.';
      })
      // getCommissionSum
      .addCase(getCommissionSum.pending, (state) => {
        state.commissionSumStatus = 'loading';
        state.commissionSumError = undefined;
      })
      .addCase(getCommissionSum.fulfilled, (state, action: PayloadAction<CommissionSumResponse>) => {
        state.commissionSumStatus = 'loaded';
        state.commissionSumResults = action.payload.data;
        state.commissionSumMeta = action.payload.meta;
        state.commissionSumExtra = action.payload.extra;
      })
      .addCase(getCommissionSum.rejected, (state, action) => {
        state.commissionSumStatus = 'failed';
        state.commissionSumError = action.payload ?? '후원금 내역 합계 조회에 실패했습니다.';
      })
      // searchCommissions
      .addCase(searchCommissions.pending, (state) => {
        state.commissionSearchStatus = 'searching';
        state.commissionSearchError = undefined;
      })
      .addCase(searchCommissions.fulfilled, (state, action: PayloadAction<CommissionSearchResponse>) => {
        state.commissionSearchStatus = 'searched';
        state.commissionSearchResults = action.payload.data;
        state.commissionSearchMeta = action.payload.meta;
      })
      .addCase(searchCommissions.rejected, (state, action) => {
        state.commissionSearchStatus = 'failed';
        state.commissionSearchError = action.payload ?? '후원금 내역 조회에 실패했습니다.';
      });
  },
});

export const { resetSettlementState, clearSearchResults, clearCommissionSum, clearCommissionSearch } = settlementSlice.actions;
export default settlementSlice.reducer;

